import SEOPageEdit from './SEOPageEdit'
import store from './store'

const dayjs = require('dayjs')
const timezone = require('dayjs/plugin/timezone')
const utc = require('dayjs/plugin/utc')
dayjs.extend(utc)
dayjs.extend(timezone)

function getRobotTag(tags, unavailableAfter) {
  // ENUM to support output ordering of tags. (Is this required?)
  const RobotsTagOrder = {
    index: 1,
    follow: 2,
    imageindex: 3,
    archive: 4,
    cache: 5,
    snippet: 6,
    translate: 7
  }

  // nocache is the same as noarchive, but less common.
  tags.cache = tags.archive
  let ua = ''
  if (unavailableAfter) {
    ua = 'unavailable_after: ' + dayjs.tz(unavailableAfter, 'UTC').format('DD MMM YY HH:mm:ss') + ' GMT'
  }

  // If all tags are included, return nothing (default).
  // If not indexing or following return none (noindex,nofollow as 'none' is not supported in all cases).
  return Object.values(tags).every(v => v) ? ua : (
    !tags.index && !tags.follow ? 'noindex,nofollow' : (
      Object.keys(tags)
        .sort((a, b) => (a === b) ? 0 : (RobotsTagOrder[a] < RobotsTagOrder[b] ? -1 : 1))
        .filter(k => !tags[k])
        .reduce((acc, cur) => acc.concat(['no' + cur]), ua ? [ua] : [])
        .join(',')
    )
  )
}

const callbacks = {
  fetchData: '$hmFetchData',
  saveData: '$seoSave',
  metaTagsCallback: '$hmMetaTagsCallback',
  linksCallback: '$hmLinksCallback',
  titleCallback: '$hmTitleCallback',
  titleTemplateCallback: '$hmTitleTemplateCallback',
  toggleEditCallback: 'toggleSEOEdit'
}

function install(Vue, options) {
  let instance = null

  if (!options.store) {
    throw new Error('Please register SEO Plugin by passing a Vuex Store')
  }
  options.store.registerModule('seo', store, { preserveState: options.store.state.seo !== undefined })

  Vue.toggleSEOEdit = function(vm, editState) {
    if (editState) {
      SEOPageEdit.asyncData({store: vm.$store, route: vm.$route, router: vm.$router})
        .then(_ => {
          const EditClass = Vue.extend(SEOPageEdit)
          instance = new EditClass({
            store: vm.$store,
            route: vm.$route,
            router: vm.$router,
            parent: vm.$root
          })
          instance.$mount()
          vm.$root.$el.appendChild(instance.$el)
        })
    }
    else {
      instance.$destroy()
      instance.$el.remove()
      vm.$forceUpdate()
      instance = null
    }
  }
  const stripLeadingSlash = path => path.charAt(0) === '/' ? path.substr(1) : path
  const getProcessedData = function(path) {
    let data = JSON.parse(JSON.stringify(this.$store.state.seo.defaults))
    path = stripLeadingSlash(path)
    if (this.$store.state.seo.cache[path] !== undefined) {
      data = JSON.parse(JSON.stringify(this.$store.state.seo.cache[path].data))
    }
    data.robots = getRobotTag(data.robots.flags, data.robots.unavailable_after)
    data.meta = ['robots', 'description'].reduce((acc, cur) => {
      if (data[cur]) acc.push({ name: cur, content: data[cur] })
      return acc
    }, [])

    // Open Graph tags. These are `property` meta tags, so they flow through the
    // existing meta injection. Site-level values come from site settings - never
    // hardcode the site name/locale (this bundle serves every tenant).
    const siteName = this.$store.getters.getSiteSetting('title')
    const locale = this.$store.getters.getSiteSetting('locale') || 'en_AU'

    data.meta.push({ vmid: 'og:title', property: 'og:title', content: data.title || siteName })
    data.meta.push({ vmid: 'og:type', property: 'og:type', content: 'website' })
    if (data.canonical) data.meta.push({ vmid: 'og:url', property: 'og:url', content: data.canonical })
    if (data.description) data.meta.push({ vmid: 'og:description', property: 'og:description', content: data.description })
    if (siteName) data.meta.push({ vmid: 'og:site_name', property: 'og:site_name', content: siteName })
    data.meta.push({ vmid: 'og:locale', property: 'og:locale', content: locale })

    data.link = data.canonical ? [{rel: 'canonical', href: data.canonical}] : []

    return ['title', 'link', 'meta'].reduce((acc, cur) => {
      if (data[cur]) acc[cur] = data[cur]
      return acc
    }, {})
  }

  Vue.prototype.$seoSave = function(seoData) {
    return this.$store.dispatch('seo/save', seoData)
      .then(function() {
        if (this.$meta) {
          this.$meta().refresh()
        }
        this.$resetCurrentHeadData(this)
      }.bind(this))
  }
  Vue.prototype.$hmFetchData = function() {
    return this.$store.dispatch('seo/load', { path: this.$route.path })
  }
  Vue.prototype.$hmMetaTagsCallback = function() {
    const seoData = getProcessedData.call(this, this.$route.path)
    return seoData.meta
  }
  Vue.prototype.$hmLinksCallback = function() {
    const seoData = getProcessedData.call(this, this.$route.path)
    return seoData.link
  }
  Vue.prototype.$hmTitleCallback = function(title) {
    const seoData = getProcessedData.call(this, this.$route.path)
    return seoData.title || title
  }
  Vue.prototype.$hmTitleTemplateCallback = titleTemplate => titleTemplate
}

export default { install, callbacks }
